import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import logoSrc from '../assets/FinemaArt.jpg';

class Particle {
  constructor(x, y, color, mobile) {
    this.baseX = x;
    this.baseY = y;
    this.x = x + (mobile ? Math.random() * 2 - 1 : Math.random() * 10 - 5);
    this.y = y + (mobile ? Math.random() * 2 - 1 : Math.random() * 10 - 5);
    this.size = mobile ? (0.6 + Math.random() * 0.2) : (2 + Math.random());
    this.density = Math.random() * 20 + 1;
    this.color = color;
    this.phase = Math.random() * Math.PI * 2;
    this.ampX = mobile ? (Math.random() * 0.5 + 0.2) : (Math.random() * 4 + 2);
    this.ampY = mobile ? (Math.random() * 0.5 + 0.2) : (Math.random() * 4 + 2);
    this.speed = Math.random() * 0.01 + 0.008;
  }

  draw(ctx) {
    ctx.fillStyle = this.color;
    ctx.beginPath();
    ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
    ctx.closePath();
    ctx.fill();
  }

  update(mouse, time) {
    const targetX = this.baseX + Math.sin(time * this.speed + this.phase) * this.ampX;
    const targetY = this.baseY + Math.cos(time * this.speed * 0.7 + this.phase) * this.ampY;
    const dx = this.x - mouse.x;
    const dy = this.y - mouse.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist < mouse.radius) {
      const force = (mouse.radius - dist) / mouse.radius;
      this.x += (dx / dist) * force * this.density;
      this.y += (dy / dist) * force * this.density;
    } else {
      this.x += (targetX - this.x) * 0.08;
      this.y += (targetY - this.y) * 0.08;
    }
  }
}

function sampleLogo(img, cssW, cssH, gap = 2) {
  const particles = [];
  const dpr = window.devicePixelRatio || 1;
  const mobile = cssW < 768;

  const logoW = mobile ? cssW * 1.1 : cssW * 0.5;
  const logoH = (img.height / img.width) * logoW;
  const logoX = cssW / 2 - logoW / 2;
  const logoY = mobile ? cssH * 0.48 - logoH / 2 : cssH / 2 - logoH / 2;

  // Use a temporary off-DOM canvas for pixel sampling (bypasses Brave fingerprint protection)
  const tmpCanvas = document.createElement('canvas');
  tmpCanvas.width = cssW * dpr;
  tmpCanvas.height = cssH * dpr;
  const tmpCtx = tmpCanvas.getContext('2d', { willReadFrequently: true });

  tmpCtx.drawImage(img, logoX * dpr, logoY * dpr, logoW * dpr, logoH * dpr);
  const imageData = tmpCtx.getImageData(0, 0, tmpCanvas.width, tmpCanvas.height);

  const scaledGap = Math.round(gap * dpr);
  const sampleChance = mobile ? 0.75 : 0.5;

  for (let py = 0; py < tmpCanvas.height; py += scaledGap) {
    for (let px = 0; px < tmpCanvas.width; px += scaledGap) {
      const i = (py * tmpCanvas.width + px) * 4;
      const r = imageData.data[i];
      const g = imageData.data[i + 1];
      const b = imageData.data[i + 2];
      const a = imageData.data[i + 3];
      if (a > 128 && (r + g + b) / 3 < 250 && Math.random() < sampleChance) {
        particles.push(new Particle(px / dpr, py / dpr, `rgb(${r},${g},${b})`, mobile));
      }
    }
  }

  return particles;
}

export default function Landing() {
  const canvasRef = useRef(null);
  const fallbackRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    let cssW = window.innerWidth;
    let cssH = window.innerHeight;
    let w, h;
    let particles = [];
    let animId;
    const mouse = { x: -9999, y: -9999, radius: 150 };
    const img = new Image();
    img.src = logoSrc;

    const init = () => {
      cssW = window.innerWidth;
      cssH = window.innerHeight;
      const isMobile = cssW < 768;
      mouse.radius = isMobile ? 80 : 150;
      const gap = isMobile ? 1 : 2;
      canvas.width = cssW * dpr;
      canvas.height = cssH * dpr;
      canvas.style.width = cssW + 'px';
      canvas.style.height = cssH + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      w = cssW;
      h = cssH;
      particles = [];
      if (img.complete && img.naturalWidth > 0) {
        try {
          const sampled = sampleLogo(img, cssW, cssH, gap);
          if (sampled.length > 0) {
            particles.push(...sampled);
          } else {
            canvas.style.display = 'none';
            if (fallbackRef.current) fallbackRef.current.style.display = 'block';
          }
        } catch {
          canvas.style.display = 'none';
          if (fallbackRef.current) fallbackRef.current.style.display = 'block';
        }
      }
    };

    img.onload = init;
    if (img.complete) init();

    const onResize = () => init();
    const onMove = (e) => { mouse.x = e.clientX; mouse.y = e.clientY; };
    const onLeave = () => { mouse.x = -9999; mouse.y = -9999; };

    const onTouchMove = (e) => {
      if (e.touches.length > 0) {
        mouse.x = e.touches[0].clientX;
        mouse.y = e.touches[0].clientY;
      }
    };
    const onTouchEnd = () => { mouse.x = -9999; mouse.y = -9999; };

    window.addEventListener('resize', onResize);
    window.addEventListener('mousemove', onMove);
    canvas.addEventListener('mouseleave', onLeave);
    canvas.addEventListener('touchmove', onTouchMove, { passive: true });
    canvas.addEventListener('touchend', onTouchEnd);

    const animate = (time) => {
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.restore();
      particles.forEach(p => {
        p.update(mouse, time);
        p.draw(ctx);
      });
      animId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('mousemove', onMove);
      canvas.removeEventListener('mouseleave', onLeave);
      canvas.removeEventListener('touchmove', onTouchMove);
      canvas.removeEventListener('touchend', onTouchEnd);
    };
  }, []);

  return (
    <div className="landing">
      <canvas ref={canvasRef} className="landing-canvas-full" />
      <img ref={fallbackRef} src={logoSrc} alt="Finema" className="landing-fallback-logo" />
      <div className="landing-overlay">
        <div className="landing-bottom-text">
          <div className="landing-cta-group">
            <p className="landing-slogan">Your content. Your value. No middlemen.</p>
            <p className="landing-desc">Premium audiovisual content on blockchain. Movies, courses, documentaries and more — pay with USDC and 90% goes directly to the creator.</p>
            <button onClick={() => navigate('/explore')} className="btn landing-enter-btn">Enter</button>
          </div>
          <div className="landing-powered">
            <span>Powered by</span>
            <svg className="landing-base-logo" viewBox="0 0 111 111" xmlns="http://www.w3.org/2000/svg">
              <circle cx="55.5" cy="55.5" r="55.5" fill="#0052FF"/>
              <path d="M55.4 93.5c-20.9 0-37.9-17-37.9-37.9s17-37.9 37.9-37.9c18.6 0 34.1 13.5 37.2 31.2H65.3c-2.7-7.5-9.9-12.9-18.4-12.9-10.8 0-19.5 8.7-19.5 19.5s8.7 19.5 19.5 19.5c8.5 0 15.7-5.4 18.4-12.9h27.4c-3.1 17.8-18.7 31.4-37.3 31.4z" fill="#fff"/>
            </svg>
            <span>Base</span>
          </div>
        </div>
      </div>
    </div>
  );
}
